import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { type AdminTarget, callAdminWith } from './api';
import { ACTIVE_CELL, CELLS, type PortalCell } from './cells';
import { adminApiEndpoint, publishableKey, supabase } from './supabase';

/**
 * One client per cell, so signing out here also ends the sessions this browser
 * holds in the other cells. Each cell is its own Supabase project with its own
 * staff and sessions: this tab's own cell uses the client it already has, and
 * every other cell gets one of its own, with its own `storageKey` so its
 * session never mixes with another cell's. Giving a colleague access in
 * another country is not done through these sessions: Ghana's server does it
 * over the headquarters link (docs/workflows/market-cell.md).
 */
export interface CellConnection extends AdminTarget {
  code: string;
  client: SupabaseClient;
}

/** A cell the build has both an address and a publishable key for. */
export function isCellConfigured(cell: PortalCell): boolean {
  return Boolean(cell.url && cell.key);
}

/** Where a cell's admin-api is: its own address if set, else the project's functions URL. */
export function adminEndpointOf(cell: PortalCell): string {
  if (cell.adminApiUrl) return cell.adminApiUrl;
  const base = (cell.url ?? '').replace(/\/$/, '');
  return `${base}/functions/v1/admin-api`;
}

/** supabase-js for a cell other than this tab's own. */
export function makeCellClient(cell: PortalCell): SupabaseClient {
  if (!cell.url || !cell.key) {
    throw new Error(`${cell.name} is not set up in this portal.`);
  }
  return createClient(cell.url, cell.key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // The page's address belongs to this tab's own cell.
      detectSessionInUrl: false,
      storageKey: cell.storageKey,
    },
  });
}

export interface CellConnections {
  get: (cell: PortalCell) => CellConnection;
  /** The connections made so far, other cells only. */
  made: () => CellConnection[];
}

/**
 * One connection per cell, made on first use. The active cell reuses the
 * client the portal already signs in with; any other cell is built once by
 * `make` and kept.
 */
export function createCellConnections(
  active: CellConnection,
  make: (cell: PortalCell) => SupabaseClient = makeCellClient,
): CellConnections {
  const others = new Map<string, CellConnection>();
  return {
    get(cell) {
      if (cell.code === active.code) return active;
      let connection = others.get(cell.code);
      if (!connection) {
        const client = make(cell);
        connection = {
          code: cell.code,
          client,
          endpoint: adminEndpointOf(cell),
          key: cell.key ?? '',
        };
        others.set(cell.code, connection);
      }
      return connection;
    },
    made: () => [...others.values()],
  };
}

const connections = createCellConnections({
  code: ACTIVE_CELL.code,
  client: supabase,
  endpoint: adminApiEndpoint,
  key: publishableKey,
});

export function connectionFor(cell: PortalCell): CellConnection {
  return connections.get(cell);
}

/** The cells in the build other than this tab's own. */
export function otherCells(): PortalCell[] {
  return CELLS.filter((cell) => cell.code !== ACTIVE_CELL.code);
}

function hasStoredSession(cell: PortalCell): boolean {
  try {
    return localStorage.getItem(cell.storageKey) !== null;
  } catch {
    return false;
  }
}

/**
 * Ends every session this browser holds in other cells: at the server where
 * it can, and always on this device. Called when the person signs out here.
 */
export async function endOtherCellSessions(): Promise<void> {
  await Promise.all(
    otherCells()
      .filter((cell) => isCellConfigured(cell) && hasStoredSession(cell))
      .map(async (cell) => {
        try {
          const connection = connectionFor(cell);
          const { data } = await connection.client.auth.getSession();
          if (data.session) {
            await callAdminWith(connection, 'session.sign_out', {}).catch(
              () => undefined,
            );
          }
          await connection.client.auth.signOut({ scope: 'local' });
        } catch {
          // The session may already be over; clearing it here is what matters.
        }
      }),
  );
}
